import { apiClient, type ApiResponse } from '@/api';
import { LEAD_API_URLS, LEAD_STATUS_API_URLS, USER_API_URLS } from '@/api/constants/api.url';
import type { DateRange } from '@/shared/components/ui/date-range-menu';
import type {
    AssignLeadInput,
    BulkImportLeadsResult,
    ChangeLeadStatusInput,
    CreateLeadInput,
    CreateLeadStatusInput,
    ImportLeadRow,
    Lead,
    LeadDetail,
    LeadDetailResponse,
    LeadListResponse,
    LeadPage,
    LeadResponse,
    LeadStatusListResponse,
    LeadStatusOption,
    LeadStatusResponse,
    ListLeadsParams,
    SendLeadMessageInput,
    SendLeadMessageResult,
    BulkMessageLeadsInput,
    BulkMessageLeadsResult,
    UpdateLeadInput,
    UpdateLeadStatusInput,
} from '@/modules/leads/types';

/** A tenant member, as returned by GET /users (used for the assign dropdown). */
export interface TenantUser {
    id:        string;
    email:     string;
    full_name: string | null;
    role:      string;
}
interface UserListResponse { users: TenantUser[] }

/** Convert a UI `DateRange` (YYYY-MM-DD, inclusive) into ISO query params. */
function rangeParams(range: DateRange | null): Record<string, string> {
    if (!range?.from || !range.to) return {};
    // `to` is inclusive in the UI — push it to the end of that day so the
    // server's half-open `[from, to)` window covers the final day.
    return {
        from: new Date(`${range.from}T00:00:00.000Z`).toISOString(),
        to:   new Date(`${range.to}T23:59:59.999Z`).toISOString(),
    };
}

export const leadsService = {
    /**
     * Fetch leads as a flat array. Used by drawers/pickers that want a broad
     * set rather than a page — pulls the API's 200-row max within the range.
     */
    async list(range: DateRange | null = null): Promise<Lead[]> {
        const { data } = await apiClient.get<ApiResponse<LeadListResponse>>(
            LEAD_API_URLS.list,
            { params: { ...rangeParams(range), limit: 200 } },
        );
        return data.data.leads;
    },

    /** Fetch one page of leads plus the total match count + pipeline stats. */
    async listPaged(params: ListLeadsParams = {}): Promise<LeadPage> {
        const { data } = await apiClient.get<ApiResponse<LeadListResponse>>(LEAD_API_URLS.list, {
            params: {
                page:      params.page ?? 1,
                limit:     params.limit ?? 25,
                status_id: params.status_id,
                q:         params.q,
                heat:      params.heat,
                from:      params.from,
                to:        params.to,
            },
        });
        return {
            leads: data.data.leads,
            total: data.data.total,
            stats: data.data.stats,
            lockedCount:  data.data.locked_count ?? 0,
            visibleLimit: data.data.visible_limit ?? null,
        };
    },
    async detail(id: string): Promise<LeadDetail> {
        const { data } = await apiClient.get<ApiResponse<LeadDetailResponse>>(LEAD_API_URLS.detail(id));
        return data.data.lead;
    },
    async create(body: CreateLeadInput): Promise<Lead> {
        const { data } = await apiClient.post<ApiResponse<LeadResponse>>(LEAD_API_URLS.create, body);
        return data.data.lead;
    },
    /** Import a chunk of leads in one request. The caller splits large files into
     *  chunks (see the import modal); each chunk is upserted server-side in a
     *  single transaction. Returns per-chunk counts + any rejected rows. */
    async bulkImport(leads: ImportLeadRow[]): Promise<BulkImportLeadsResult> {
        const { data } = await apiClient.post<ApiResponse<BulkImportLeadsResult>>(
            LEAD_API_URLS.bulkImport, { leads },
        );
        return data.data;
    },
    async update(id: string, body: UpdateLeadInput): Promise<Lead> {
        const { data } = await apiClient.patch<ApiResponse<LeadResponse>>(LEAD_API_URLS.update(id), body);
        return data.data.lead;
    },
    async remove(id: string): Promise<void> {
        await apiClient.delete(LEAD_API_URLS.remove(id));
    },
    async changeStatus(id: string, body: ChangeLeadStatusInput): Promise<void> {
        await apiClient.post(LEAD_API_URLS.status(id), body);
    },
    async assign(id: string, body: AssignLeadInput): Promise<void> {
        await apiClient.post(LEAD_API_URLS.assign(id), body);
    },
    async addNote(id: string, body: string, visibility: 'team' | 'private' = 'team'): Promise<void> {
        await apiClient.post(LEAD_API_URLS.notes(id), { body, visibility });
    },
    async sendMessage(id: string, input: SendLeadMessageInput): Promise<SendLeadMessageResult> {
        const { data } = await apiClient.post<ApiResponse<SendLeadMessageResult>>(
            LEAD_API_URLS.message(id), input,
        );
        return data.data;
    },
    async bulkMessage(input: BulkMessageLeadsInput): Promise<BulkMessageLeadsResult> {
        const { data } = await apiClient.post<ApiResponse<BulkMessageLeadsResult>>(
            LEAD_API_URLS.bulkMessage, input,
        );
        return data.data;
    },
    async listStatuses(): Promise<LeadStatusOption[]> {
        const { data } = await apiClient.get<ApiResponse<LeadStatusListResponse>>(LEAD_STATUS_API_URLS.list);
        return data.data.statuses;
    },
    async createStatus(body: CreateLeadStatusInput): Promise<LeadStatusOption> {
        const { data } = await apiClient.post<ApiResponse<LeadStatusResponse>>(LEAD_STATUS_API_URLS.create, body);
        return data.data.status;
    },
    async updateStatus(id: string, body: UpdateLeadStatusInput): Promise<LeadStatusOption> {
        const { data } = await apiClient.patch<ApiResponse<LeadStatusResponse>>(LEAD_STATUS_API_URLS.update(id), body);
        return data.data.status;
    },
    async removeStatus(id: string): Promise<void> {
        await apiClient.delete(LEAD_STATUS_API_URLS.remove(id));
    },
    async listUsers(): Promise<TenantUser[]> {
        const { data } = await apiClient.get<ApiResponse<UserListResponse>>(USER_API_URLS.list);
        return data.data.users;
    },
};
